import { GameEvents } from '../GameEvents';
import type EventBus from '../EventBus';
import getLogger from '../logger';

const logger = getLogger('CurationMode');
const DEV_SERVER_URL = 'http://localhost:3001';

/**
 * Dev-only handler.  MUST be instantiated BEFORE GameplayHandler / ProgressHandler /
 * AnalyticsHandler so its dispatch listeners are registered first and can consume.
 */
export default class CurationMode {
  private state: any;
  private slice: any;
  private eventBus: EventBus;

  constructor(state: any, slice: any, eventBus: EventBus) {
    this.state = state;
    this.slice = slice;
    this.eventBus = eventBus;

    // These three consume (return true) – suppresses production handlers.
    // Arrow wrappers must forward the return value so dispatch() sees it.
    this.eventBus.on(GameEvents.LEVEL_WON, () => this._onLevelWon());
    this.eventBus.on(GameEvents.LEVEL_LOST, () => this._onLevelLost());
    this.eventBus.on(GameEvents.RESTART_GAME, () => this._onRestart());

    this.eventBus.on(GameEvents.PLAY_CANDIDATE, (payload: any) => this._onPlayCandidate(payload));
    this.eventBus.on(GameEvents.CURATION_NEXT_CANDIDATE, () => this._onNextCandidate());
    this.eventBus.on(GameEvents.EXIT_CURATION, () => this._onExitCuration());
    this.eventBus.on(GameEvents.CURATION_SAVED, (payload: any) => this._onCurationSaved(payload));
  }

  _onLevelWon() {
    if (!this.slice.currentCandidateId) return; // not in curation – don't consume
    this.slice.levelEnded = 'win';
    return true; // consume
  }

  _onLevelLost() {
    if (!this.slice.currentCandidateId) return;
    this.slice.levelEnded = 'lose';
    return true;
  }

  _onRestart() {
    if (!this.slice.currentCandidateId) return;
    // Replay current candidate
    const manifest = this.slice.candidateManifest;
    const candidate = manifest?.candidates?.find((c: any) => c.id === this.slice.currentCandidateId);
    this._playCandidate({
      candidateId: this.slice.currentCandidateId,
      existingCuration: candidate?.curation,
    });
    return true;
  }

  async _onPlayCandidate({ candidateId, existingCuration }: { candidateId: string; existingCuration?: any }) {
    logger.info('Playing candidate:', candidateId);

    const manifest = await this._loadCandidateManifest();
    if (!manifest) {
      logger.error('Could not load manifest');
      return;
    }

    const index = manifest.candidates.findIndex((c: any) => c.id === candidateId);
    if (index === -1) {
      logger.error(`Candidate ${candidateId} not found in manifest`);
      return;
    }

    this._playCandidate({ candidateId, existingCuration });
  }

  private _playCandidate({ candidateId, existingCuration }: { candidateId: string; existingCuration?: any }) {
    this.slice.currentCandidateId = candidateId;
    this.slice.currentCandidateIndex = this.slice.candidateManifest
      ? this.slice.candidateManifest.candidates.findIndex((c: any) => c.id === candidateId)
      : 0;
    this.slice.levelEnded = null;

    try {
      const levelModule = require(`../../../content/dist/candidates/${candidateId}.json`);
      const levelData = levelModule.default || levelModule;

      this.eventBus.emit(GameEvents.PLAY_LEVEL_FROM_DATA, {
        levelKey: `candidate:${candidateId}`,
        levelData,
      });
    } catch (e) {
      logger.error('Failed to load candidate:', e);
    }
  }

  async _onNextCandidate() {
    if (!this.slice.currentCandidateId) return;

    const manifest = this.slice.candidateManifest;
    if (!manifest) return;

    const candidates = manifest.candidates;
    let nextIndex = this.slice.currentCandidateIndex + 1;

    // Find next uncurated candidate
    for (let i = 0; i < candidates.length; i++) {
      const checkIndex = (nextIndex + i) % candidates.length;
      const candidate = candidates[checkIndex];
      if (!candidate.curation?.status) {
        this._playCandidate({
          candidateId: candidate.id,
          existingCuration: candidate.curation,
        });
        return;
      }
    }

    // All curated, go to next in sequence
    if (nextIndex >= candidates.length) {
      nextIndex = 0;
    }
    const nextCandidate = candidates[nextIndex];
    this._playCandidate({
      candidateId: nextCandidate.id,
      existingCuration: nextCandidate.curation,
    });
  }

  _onExitCuration() {
    this.slice.currentCandidateId = null;
    this.slice.candidateManifest = null;
    this.slice.currentCandidateIndex = 0;
    this.slice.levelEnded = null;
    this.eventBus.emit(GameEvents.SET_SCREEN, 'home');
  }

  _onCurationSaved({ candidateId, curation }: { candidateId: string; curation: any }) {
    if (!this.slice.candidateManifest) return;
    const candidate = this.slice.candidateManifest.candidates.find((c: any) => c.id === candidateId);
    if (candidate) {
      candidate.curation = curation;
    }
  }

  private async _loadCandidateManifest(): Promise<any> {
    if (this.slice.candidateManifest) {
      return this.slice.candidateManifest;
    }
    try {
      const manifestModule = require('../../../content/dist/candidates/manifest.json');
      const manifest = manifestModule.default || manifestModule;

      // Load curation data from dev server
      let curationData: Record<string, any> = {};
      try {
        const curationResponse = await fetch(`${DEV_SERVER_URL}/api/curation`);
        if (curationResponse.ok) {
          curationData = await curationResponse.json();
        }
      } catch (e) {
        logger.warn('Dev server not available');
      }

      // Merge
      manifest.candidates = manifest.candidates.map((c: any) => ({
        ...c,
        curation: curationData[c.id] || null,
      }));

      this.slice.candidateManifest = manifest;
      return manifest;
    } catch (e) {
      logger.error('Failed to load candidate manifest:', e);
      return null;
    }
  }
}
